import React from 'react';
import { useEffect, useState, useRef } from 'react';
import { formatToLocalISO } from './utils/general.js'

const LINES = 20;

function epoch(time, type, format) {
    let epoch_in_s = false;
    if (type == 0) {
        epoch_in_s = true;
    }
    return epoch_in_s ? formatToLocalISO(time * 1000, format) : formatToLocalISO(time, format);
}

function type5_exit(exit_code) {
    switch(exit_code) {
    case 12: return "Visto un target in piedi";
    case 23: return "Nessun movimento";
    case 24: return "Heigth transition e/o Sensitivity map";
    case 26:
    case 27:
        return "Uscita per Mitigator Algorithm"
    default: return exit_code;
    }
}

function backgroundRow(test, type, status) {
    if (test) return "gray";
    switch(type) {
    case 5: return (status === 'calling') ? "red" : "salmon";
    case 8: return (status === 'calling') ? "red" : "orange";
    case 12: return "cyan";
    case 13: return "lightgreen";
    default: return "";
    }
}

const Log = ({ getLoggingDataRange, eraseLogging, NData, page, setPage, nomeFile, falls, setFalls }) => {
    const [dbLines, setDbLines] = useState([]);

    function pageDown() {
        if (page > 1) setPage(page - 1);
    }
    function pageUp() {
        if (NData >= ((page) * LINES + 1)) setPage(page + 1);
    }

    const [blink, setBlink] = useState(false);
    const skipsLeft = useRef(process.env.NODE_ENV === "development" ? 2 : 1);

    useEffect(() => {
        (async () => {
            if (NData == 0) return;
            const from = (page-1) * LINES + 1;
            const to = from + 19;
            const lines = await getLoggingDataRange(from, to);
            const dbLines = Array.isArray(lines) ? lines : JSON.parse(lines);
            // console.log(dbLines, Array.isArray(dbLines));
            setDbLines(dbLines);
        })();
    }, [page, NData]);

    useEffect(() => {
        if (skipsLeft.current > 0) {
            skipsLeft.current -= 1;
            return;
        }
        setBlink(true);
        const t = setTimeout(() => setBlink(false), 1000);
        return () => clearTimeout(t);
    }, [NData]);

    function totPages() {
        const n_pages = Math.floor((NData-1)/LINES)+1;
        return (n_pages > 0) ? n_pages : 1;
    }

    async function downloadAsJson() {
        const rows = await getLoggingDataRange(1, NData); // array of objects
        const json = JSON.stringify(rows, null, 2); // pretty-print with 2 spaces
        const blob = new Blob([json], { type: "application/json" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = nomeFile + ".json";
        a.click();
        URL.revokeObjectURL(a.href);
    }

    async function eraseTable() {
        if (window.confirm("Sicuro di voler cancellare lo storico del Log di questo sensore? Non si può tornare indietro!")) {
            skipsLeft.current = process.env.NODE_ENV === "development" ? 2 : 1;
            await eraseLogging();
            setDbLines([]);
        }
    };

    function fallSelect(fall_event) {
        if (!fall_event.fall) return;
        // console.log("cliccato", fall_event.id)
        if (isSelected(fall_event.id)) {
            setFalls(prev => prev.filter(x => x.id !== fall_event.id));
        } else {
            setFalls(prev => [...prev, {id: fall_event.id, type: fall_event.type, x_cm: fall_event.x_cm, y_cm: fall_event.y_cm, z_cm: fall_event.z_cm}]);
        }
    };

    function isSelected(id) {
        return falls.some(item => item.id === id);
    }

    return (
        <div className="logging-wrapper">
            <div className={`logging-top-area ${blink ? "blink" : ""}`}>
                <div className="logging-list">
                    {/* Header row */}
                    <div className="logging-row logging-row--header">
                        <div className="logging-cell">#</div>
                        <div className="logging-cell">Day</div>
                        <div className="logging-cell">Time</div>
                        <div className="logging-cell">type</div>
                        <div className="logging-cell">Info</div>
                        <div className="logging-cell">Details</div>
                    </div>

                    {/* Data rows */}
                    {Array.from({ length: LINES }).map((_, i) => {
                        const line = dbLines[i]; // may be undefined
                        if (line === undefined) {
                            return (
                                <div className="logging-row logging-row--placeholder" key={i}>
                                    <div className="logging-cell"></div>
                                    <div className="logging-cell"></div>
                                    <div className="logging-cell"></div>
                                    <div className="logging-cell"></div>
                                </div>
                            );
                        }

                        const fall_event = { fall: false};
                        const rowNumber = (page - 1) * LINES + i + 1;
                        const type = line?.payload_type ? line.payload_type : line?.type;
                        const time_type = line.care4u ? 0 : line.type;
                        let info = '';
                        let infoProd = '';
                        if (type == 13) {
                            info = line.doorEventType;
                            infoProd = " id:" + line.doorId;
                        }
                        if (type == 5) {
                            info = line.status;
                            infoProd = line.care4u
                                       ? ""
                                       : (((line.status !== "fall_exit") && (line.status !== "finished"))
                                          ? (line.fallLocX_cm / 100) + " : "+ (line.fallLocY_cm / 100) + " : "+ (line.fallLocZ_cm / 100)
                                          : (line.status === "fall_exit") ? type5_exit(line.exitReason) : ""
                                         );
                            if (info === 'fall_detected') {
                                fall_event.fall = true;
                                fall_event.id = line.id;
                                fall_event.type = 5;
                                fall_event.x_cm = line.fallLocX_cm;
                                fall_event.y_cm = line.fallLocY_cm;
                                fall_event.z_cm = line.fallLocZ_cm;
                            }
                        }
                        if (type == 8) {
                            info = line.status;
                            infoProd = line.care4u
                                       ? ""
                                       : (line.fallLocX_cm / 100) + " : "+ (line.fallLocY_cm / 100) + " : "+ (line.fallLocZ_cm / 100) +
                                         " -- #" + line.suspectedEventsCounter + " conf. " + line.lastEventConfidence.toFixed(3);
                            if (info === 'fall_suspected') {
                                fall_event.fall = true;
                                fall_event.id = line.id;
                                fall_event.type = 8;
                                fall_event.x_cm = line.fallLocX_cm;
                                fall_event.y_cm = line.fallLocY_cm;
                                fall_event.z_cm = line.fallLocZ_cm;
                            }
                        }
                        let timestamp = line.timestamp;
                        if (type == 5) timestamp = line.statusUpdateTimestamp;
                        if (type == 13) timestamp = line.recordTime;
                        const eventDay = line
                            ? epoch(timestamp ?? line.recordTime, time_type, "dm")
                            : '';
                        const eventTime = line
                            ? epoch(timestamp ?? line.recordTime, time_type, "hms")
                            : '';

                        return (
                            <div 
                                onClick={() => {fallSelect(fall_event)}} 
                                className="logging-row" 
                                style={{
                                    background: backgroundRow(line.care4u, line.payload_type, line.status),
                                    border: isSelected(line.id) ? "2px solid red" : ""
                                }} 
                                key={i}>
                                <div className="logging-cell">{rowNumber}</div>
                                <div className="logging-cell">{eventDay}</div>
                                <div className="logging-cell">{eventTime}</div>
                                <div className="logging-cell">{type}</div>
                                <div className="logging-cell">{info}</div>
                                <div className="logging-cell-details">{infoProd}</div>
                            </div>
                        );
                    })}

                </div>
            </div>

            <div className="logging-bottom-area">
                <label>Eventi: {NData}</label>
                <button style={{ marginLeft: "10px", marginRight: "10px" }} onClick={() => pageDown()}>&lt;</button>
                <label>{page}/{totPages()}</label>
                <button style={{ marginLeft: "10px" }} onClick={() => pageUp()}>&gt;</button>
                <label style={{ marginLeft: "10px", backgroundColor: "gray", borderRadius: "4px" }}>TEST</label>
                <label style={{ marginLeft: "20px", backgroundColor: "red", borderRadius: "4px" }}>calling</label>
                <label style={{ marginLeft: "20px", backgroundColor: "salmon", borderRadius: "4px" }}>fall</label>
                <label style={{ marginLeft: "10px", backgroundColor: "orange", borderRadius: "4px" }}>sensitive fall</label>
                <label style={{ marginLeft: "10px", backgroundColor: "cyan", borderRadius: "4px" }}>out-of-bed</label>
                <label style={{ marginLeft: "10px", backgroundColor: "lightgreen", borderRadius: "4px" }}>door</label>
            </div>
            <div>
                <button style={{ marginLeft: "10px", marginRight: "10px", backgroundColor: "gray" }} onClick={() => eraseTable()}>Cancella</button>
                <button style={{ marginLeft: "10px", marginRight: "10px", backgroundColor: "green" }} onClick={() => downloadAsJson()}>Esporta</button>
                <button style={{ marginLeft: "80px", marginRight: "10px", backgroundColor: "darkgray" }} onClick={() => setFalls([])}>Pulisci Cadute</button>
            </div>
        </div>
    );
}

export default Log;
